import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { Lead } from '@marnic/shared';
import { formatUKForDisplay } from '@marnic/shared';
import { softphoneService } from '../softphone/service';
import { useSoftphone } from '../store/softphone';
import { useAuthStore } from '../store/auth';
import { LeadImportModal } from '../components/LeadImportModal';
import { useWhatsapp } from '../store/whatsapp';

interface LeadsResponse {
  total: number;
  items: Lead[];
}

export function Leads(): JSX.Element {
  const [query, setQuery] = useState('');
  const [sourceType, setSourceType] = useState('');
  const [showImport, setShowImport] = useState(false);
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.user);
  const setLead = useSoftphone((s) => s.setLead);
  const navigate = useNavigate();
  const setActiveConversation = useWhatsapp((s) => s.setActiveConversation);
  const leads = useQuery({
    queryKey: ['leads', query, sourceType],
    queryFn: () => api.get<LeadsResponse>(
      `/leads?query=${encodeURIComponent(query)}&limit=100${sourceType ? `&sourceType=${encodeURIComponent(sourceType)}` : ''}`,
    ),
  });

  const callLead = async (lead: Lead): Promise<void> => {
    setLead(lead);
    await softphoneService.dial({ toRaw: lead.phoneE164, leadId: lead.id });
  };

  const openWhatsapp = async (lead: Lead): Promise<void> => {
    const result = await api.get<{ total: number; items: Array<{ id: string }> }>(
      `/whatsapp/conversations?leadId=${lead.id}&status=open&limit=1`,
    );
    if (result.total > 0 && result.items[0]) {
      setActiveConversation(result.items[0].id);
    } else {
      setActiveConversation(null);
    }
    void navigate('/whatsapp');
  };

  const promoteColdLead = async (lead: Lead): Promise<void> => {
    await api.post(`/leads/${lead.id}/promote-cold-lead`);
    await qc.invalidateQueries({ queryKey: ['leads'] });
  };

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Leads</h1>
        <div className="flex items-center gap-2">
          <input
            className="input w-64"
            placeholder="Search by name, company, phone, email…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <select className="input w-44" value={sourceType} onChange={(e) => setSourceType(e.target.value)}>
            <option value="">All sources</option>
            <option value="perfex_lead">Perfex leads</option>
            <option value="perfex_customer">Customers</option>
            <option value="perfex_contact">Contacts</option>
            <option value="perfex_cold_lead">Cold leads</option>
          </select>
          {me?.role !== 'agent' && (
            <button className="btn-secondary" onClick={() => setShowImport(true)}>
              Import
            </button>
          )}
        </div>
      </header>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm whitespace-nowrap">
          <thead className="bg-ink-50 text-left text-xs uppercase tracking-wider text-ink-500">
            <tr>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Company</th>
              <th className="px-4 py-2">Phone</th>
              <th className="px-4 py-2">Email</th>
              <th className="px-4 py-2">Source</th>
              <th className="px-4 py-2 w-48">Action</th>
            </tr>
          </thead>
          <tbody>
            {leads.data?.items.map((lead) => (
              <tr key={lead.id} className="border-t border-ink-100 hover:bg-ink-50">
                <td className="px-4 py-2">
                  <Link to={`/leads/${lead.id}`} className="hover:underline text-ink-900">
                    {lead.firstName} {lead.lastName}
                  </Link>
                </td>
                <td className="px-4 py-2">{lead.company ?? '–'}</td>
                <td className="px-4 py-2 font-mono">{formatUKForDisplay(lead.phoneE164)}</td>
                <td className="px-4 py-2 truncate">{lead.email ?? '–'}</td>
                <td className="px-4 py-2 text-xs text-ink-500">
                  {lead.primarySourceType?.replace(/_/g, ' ') ?? lead.sources?.find((s) => s.active)?.sourceType.replace(/_/g, ' ') ?? 'manual'}
                </td>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-1">
                    <button type="button" className="btn-primary text-xs" onClick={() => void callLead(lead)}>
                      📞 Call
                    </button>
                    <button type="button" className="btn-secondary text-xs" onClick={() => void openWhatsapp(lead)}>
                      💬 WhatsApp
                    </button>
                    {me?.role !== 'agent' && lead.sources?.some((s) => s.active && s.sourceType === 'perfex_cold_lead') && (
                      <button type="button" className="btn-secondary text-xs" onClick={() => void promoteColdLead(lead)}>
                        Promote
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {leads.data?.items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-ink-500">No leads.</td></tr>
            )}
          </tbody>
        </table>
        </div>
      </div>

      <LeadImportModal open={showImport} onClose={() => setShowImport(false)} />
    </div>
  );
}
